import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize, unmaskNumber } from '@givebutter/util';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { faTablePicnic, faTicket, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTableSeatingStats, updateSeatingArrangement } from 'api/events';
import { indexToSeatNumber } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { eventQueryKeys } from 'queries/event-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface TableLayoutSetupDialogProps extends Dialog.DialogProps {}

interface TableSetupFormValues {
    tablesQty: number;
    seatsPerTable: number;
}

const MANUAL_SEATING_VALUES: TableSetupFormValues = {
    tablesQty: 1,
    seatsPerTable: 4,
};

const DEFAULT_SEATING_VALUES: TableSetupFormValues = {
    tablesQty: 24,
    seatsPerTable: 8,
};

const TableLayoutSetupDialog = (props: TableLayoutSetupDialogProps) => {
    const { children, ...rest } = props;
    const toast = useToast();

    const methods = useGivebutterForm<TableSetupFormValues>({
        defaultValues: DEFAULT_SEATING_VALUES,
    });

    const { handleSubmit, control, path, setError } = methods;

    const { data: event } = useCurrentCampaignEvent();

    const { data: statistics } = useSuspenseQuery({
        queryKey: eventQueryKeys.seatingStats(event.id),
        queryFn: () => fetchTableSeatingStats(event.id),
        refetchOnWindowFocus: false,
    });

    const ticketsSold = statistics?.tickets?.total ?? 0;

    const { mutate: createTables, isPending } = useMutation({
        mutationFn: (values: TableSetupFormValues) =>
            updateSeatingArrangement(event.id, toRequest(values)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.eventById(event.id) });
        },
        onError: (error: GivebutterAxiosError) => {
            const errorMessage = error.response?.data?.message || 'Failed to create tables';
            toast.error(errorMessage);

            const errors = error.response?.data?.errors;
            if (errors && typeof errors === 'object') {
                Object.entries(errors).forEach(([field, messages]) => {
                    if (field === 'tablesQty' || field === 'seatsPerTable') {
                        setError(field as keyof TableSetupFormValues, {
                            message: Array.isArray(messages) ? messages[0] : messages,
                        });
                    }
                });
            }
        },
    });

    const onSubmit = (values: TableSetupFormValues) => {
        createTables(values);
    };

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content
                    className="max-w-2xl"
                    onInteractOutside={e => {
                        e.preventDefault();
                    }}
                >
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex flex-col gap-4 px-4 py-8"
                        >
                            <Dialog.Close />
                            <Dialog.Header>
                                <Dialog.Title className="flex w-full flex-col sm:flex-row sm:items-center sm:gap-4">
                                    <h3 className="text-2xl">Tables &amp; seats</h3>
                                    <span className="flex items-center gap-1.5 text-sm font-medium sm:ml-auto">
                                        <FontAwesomeIcon
                                            className="text-brand-violet-primary"
                                            icon={faTicket}
                                            size="sm"
                                            aria-hidden="true"
                                        />
                                        {ticketsSold} {pluralize(ticketsSold, 'Ticket')} sold
                                    </span>
                                </Dialog.Title>
                                <Dialog.Description>
                                    Start with a quick table and seat setup, or skip and build your
                                    layout from scratch.
                                </Dialog.Description>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Box className="flex flex-col gap-6 p-4">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="flex gap-3">
                                            <Avatar.Root size="sm">
                                                <Avatar.Fallback aria-label="Table picnic icon">
                                                    <FontAwesomeIcon
                                                        className="text-gray-500"
                                                        icon={faTablePicnic}
                                                    />
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                            <div>
                                                <h3 className="font-medium">Number of tables</h3>
                                                <p className="text-sm text-gray-500">
                                                    How many tables would you like to start with?
                                                </p>
                                            </div>
                                        </div>
                                        <Form.TextInputRoot
                                            control={control}
                                            name="tablesQty"
                                            rules={{
                                                required: 'Number of tables is required.',
                                                validate: {
                                                    min: value => {
                                                        const unmasked = unmaskNumber(value);
                                                        return unmasked >= 1 || 'Minimum 1 table.';
                                                    },
                                                    max: value => {
                                                        const unmasked = unmaskNumber(value);
                                                        return (
                                                            unmasked <= 100 || 'Maximum 100 tables.'
                                                        );
                                                    },
                                                },
                                            }}
                                            inputRootProps={{
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm
                                                className="w-16 text-center"
                                                placeholder="24"
                                            />
                                        </Form.TextInputRoot>
                                    </div>
                                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                        <div className="flex gap-3">
                                            <Avatar.Root size="sm">
                                                <Avatar.Fallback aria-label="User icon">
                                                    <FontAwesomeIcon
                                                        className="text-gray-500"
                                                        icon={faUser}
                                                    />
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                            <div>
                                                <h3 className="font-medium">Seats per table</h3>
                                                <p className="text-sm text-gray-500">
                                                    How many seats at each table?{' '}
                                                </p>
                                            </div>
                                        </div>
                                        <Form.TextInputRoot
                                            control={control}
                                            name="seatsPerTable"
                                            rules={{
                                                required: 'Number of seats is required.',
                                                validate: {
                                                    min: value => {
                                                        const unmasked = unmaskNumber(value);
                                                        return unmasked >= 1 || 'Minimum 1 seat.';
                                                    },
                                                    max: value => {
                                                        const unmasked = unmaskNumber(value);
                                                        return (
                                                            unmasked <= 50 || 'Maximum 50 seats.'
                                                        );
                                                    },
                                                },
                                            }}
                                            inputRootProps={{
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm
                                                className="w-16 text-center"
                                                placeholder="8"
                                            />
                                        </Form.TextInputRoot>
                                    </div>
                                </Box>

                                <div className="flex flex-col gap-1">
                                    <Form.Error
                                        name={path('tablesQty')}
                                        className="text-sm text-red-600"
                                    />
                                    <Form.Error
                                        name={path('seatsPerTable')}
                                        className="text-sm text-red-600"
                                    />
                                </div>

                                <div className="flex w-full flex-col flex-wrap items-center justify-between gap-4 sm:flex-row">
                                    <Dialog.Cancel asChild>
                                        <Button
                                            variant="none"
                                            color="neutral"
                                            className="flex items-center gap-2"
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                            <span>Back</span>
                                        </Button>
                                    </Dialog.Cancel>

                                    <div className="xs:flex-row flex justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            color="neutral"
                                            className="xs:w-auto w-full"
                                            onClick={() => onSubmit(MANUAL_SEATING_VALUES)}
                                            disabled={isPending}
                                        >
                                            {isPending
                                                ? 'Creating table...'
                                                : 'Build layout manually'}
                                        </Button>
                                        <Form.SubmitButton disabled={isPending}>
                                            {isPending ? 'Creating tables...' : 'Continue'}
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            </Dialog.Body>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const toRequest = (values: TableSetupFormValues) => {
    const tablesQty = unmaskNumber(values.tablesQty);
    const seatsPerTable = unmaskNumber(values.seatsPerTable);

    const seating_groups = Array.from({ length: tablesQty }, (_, index) => ({
        capacity: seatsPerTable,
        name: `Table ${indexToSeatNumber(index)}`,
        number: indexToSeatNumber(index),
        locked: false,
    }));

    return { seating_groups };
};

const TableLayoutSetupDialogTrigger = Dialog.Trigger;
const Root = TableLayoutSetupDialog;

const Trigger = TableLayoutSetupDialogTrigger;

export { TableLayoutSetupDialog, TableLayoutSetupDialogTrigger, Root, Trigger };
